import { Button } from "../../../f7";
import { useResource } from "../../../hooks/useResource";
import { Card, Field, Fields } from "../../../components/ui";
import type { AppConfig } from "../../../config";
import { ProductSection, StoredAs } from "../../../products/PerProduct";
import {
  labeebStandingOf,
  labeebTenantQuotaPath,
  type LabeebCatalog,
  type LabeebTenantDetail,
  type LabeebTenantQuota,
} from "../../../api/labeeb";
import { LabeebHistory } from "../../../components/labeeb/LabeebHistory";
import { LabeebQuotaControl } from "../../../components/labeeb/LabeebQuotaControl";
import { LabeebRules } from "../../../components/labeeb/LabeebRules";
import { LabeebStandingControl } from "../../../components/labeeb/LabeebStandingControl";
import { Badge } from "../../../components/ui";
import { LabeebCapabilities } from "./LabeebCapabilities";
import { LabeebEnvelope } from "./LabeebEnvelope";
import { LabeebFeedbackPanel } from "./LabeebFeedbackPanel";
import { LabeebUsage } from "./LabeebUsage";
import { labeebMissingCopy } from "./labeebMissing";
import { LabeebUsersPanel } from "./LabeebUsersPanel";

/**
 * Labeeb's half of the record, rendered INDEPENDENTLY of MCP's.
 *
 * It used to sit inside `mcpState.kind === "ready"`, so a merchant Labeeb
 * knows and MCP does not — which /tenants now lists, and which is the normal
 * case for a merchant that has never used a tool — showed "Not seen recently"
 * and nothing else. Everything Labeeb held about them was hidden behind
 * another product's 404.
 */
export function LabeebRecord({
  config,
  tenantId,
  labeeb,
  labeebCatalog,
  mcpTier,
}: {
  config: AppConfig;
  tenantId: string;
  labeeb: ReturnType<typeof useResource<LabeebTenantDetail>>;
  labeebCatalog: ReturnType<typeof useResource<LabeebCatalog>>;
  /** MCP's stored tier, or null when MCP has never seen this merchant. */
  mcpTier: string | null;
}) {
  const ref = tenantId;

  // The tier is its own document, `GET /admin/tenant/{id}/quota`. Read once
  // here for both the "Stored as" pair and the envelope below it.
  const quota = useResource<LabeebTenantQuota>(config, labeebTenantQuotaPath(ref), [ref]);
  // null: Labeeb answered and holds no tier. undefined: we could not look,
  // which must never read as "no row".
  const labeebTier =
    quota.state.kind === "ready"
      ? (quota.state.data?.tier_id ?? null)
      : quota.state.kind === "notfound"
        ? null
        : undefined;

  // `has_config_item` is gone: a business is in Labeeb's list or it is not,
  // and a detail read either resolves or 404s. "Never used Labeeb" is no
  // longer something the console can infer from a field, so it does not
  // claim to — a merchant Labeeb returns is treated as known.

  return (
          <ProductSection id="labeeb" note="Tier, the envelope it grants, capabilities and usage">
            <div className="flex flex-col gap-4">
              <Card title="Plan tier">
                <Fields>
                  <Field label="Labeeb">
                    <LabeebStandingLine resource={labeeb} />
                  </Field>
                  {/* THE one place both tiers appear. MCP's block reports
                      only MCP, so this is where a tier split — Pro in one
                      product, Plus in the other — stays visible. */}
                  <Field label="Stored as">
                    <StoredAs stored={{ mcp: mcpTier, labeeb: labeebTier }} />
                  </Field>
                </Fields>

                {/* The tier is its own document now, with its own version,
                    so it gets its own editor reading and writing
                    `/admin/tenant/{id}/quota`. The enrolment gate that used
                    to sit here is gone: it existed because a Labeeb PATCH
                    silently CREATED a TenantConfigs row, enrolling a merchant
                    by being looked at. The new API has no such side effect —
                    a quota write names a business that already exists, and
                    404s when it does not. */}
                <div className="mt-4">
                  <LabeebQuotaControl config={config} tenantId={ref} catalog={labeebCatalog} />
                </div>
              </Card>

              <LabeebEnvelope config={config} quota={quota} catalog={labeebCatalog} />

              <LabeebCapabilities config={config} tenantId={ref} />

              <LabeebRules config={config} tenantId={ref} />

              <LabeebUsage config={config} tenantId={ref} />

              <LabeebUsersPanel config={config} tenantId={ref} />

              <LabeebFeedbackPanel config={config} tenantId={ref} />

              <Card
                title="Recent changes"
                aside={
                  <Button plain href={`/tenants/${encodeURIComponent(ref)}/history`}>
                    Full Labeeb history
                  </Button>
                }
              >
                <LabeebHistory config={config} tenantId={ref} preview={5} />
              </Card>

              <Card title="Access">
                {/* `GET`/`PATCH /admin/tenant/{id}/standing`: a suspended
                    business has its NEXT request refused. The same control
                    is on /policy/access, deliberately: one write, two doors,
                    matching how MCP blocking already works in this console. */}
                <LabeebStandingControl config={config} tenantId={ref} />
              </Card>

            </div>
          </ProductSection>
  );
}

/**
 * Labeeb's standing for this merchant, or an honest account of why there isn't one.
 *
 * A product 404 means Labeeb has no record of this business — an unconnected
 * business has nothing to show. A gateway 404 is a route that is not mounted,
 * which is a different sentence. A lookup that could not be made stays
 * "Could not read".
 */
function LabeebStandingLine({
  resource,
}: {
  resource: ReturnType<typeof useResource<LabeebTenantDetail>>;
}) {
  const state = resource.state;

  if (state.kind === "loading") {
    return <span className="text-xs text-slate-500 dark:text-slate-400">Loading…</span>;
  }
  if (state.kind === "signedout") {
    return <span className="text-xs text-slate-500 dark:text-slate-400">Signed out</span>;
  }
  if (state.kind === "denied") {
    return (
      <span className="text-xs text-slate-500 dark:text-slate-400">
        You cannot read Labeeb's tenant config.
      </span>
    );
  }
  if (state.kind === "notfound") {
    return (
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {labeebMissingCopy(state.error)}
      </span>
    );
  }
  if (state.kind === "unavailable" || state.kind === "invalid" || state.kind === "error") {
    // "Could not look" is NOT "there is nothing" — the 404 sentence here
    // would claim a fact we failed to read.
    return (
      <span className="text-xs text-amber-700 dark:text-amber-400">
        Could not read Labeeb's tier. {state.error.message}
      </span>
    );
  }

  const d = state.data;
  // THE TIER NO LONGER LIVES ON THIS ROUTE. `GET /admin/tenant/{id}` carries
  // identity and standing; the tier is its own document at
  // `GET /admin/tenant/{id}/quota`, with its own version to write against.
  // What this cell can honestly report from the detail read is standing.
  // Through `labeebStandingOf`, never `d.status`: the DETAIL route nests
  // standing (TenantSummaryOut) while a list row carries it flat (TenantOut).
  // Reading the flat field here returned undefined against the real server,
  // so an active merchant rendered as "unknown".
  const standing = labeebStandingOf(d);
  if (standing?.status === "suspended") {
    return <Badge tone="red">suspended</Badge>;
  }

  return (
    <>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {standing?.status === "active" ? "Active" : (standing?.status ?? "unknown")}
      </span>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Tier and domains are below — each is its own document now, with its own
        version.
      </p>
    </>
  );
}
